/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { AngularFireAction, DatabaseSnapshot } from '@angular/fire/compat/database';
import { of, EMPTY, Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { SplitData } from '@touchplan/common';
import { FancyAction } from '../base/FancyAction';
import { FancyFirebaseList } from '../base/FancyFirebaseList';
import { FancyActionTypes } from '../base/FancyActionTypes';
import { monitorSubList } from '../base/monitorSubList';
import { DefaultActionTypes } from './DefaultActionTypes';
import { DefaultActionHandlers } from './DefaultActionHandlers';

export class FancyFirebaseSplitDataList<
	TModelFields extends object,
	TModelRenderTypes extends string,
	TModel extends SplitData<TModelFields, TModelRenderTypes>
> extends FancyFirebaseList<TModel, FancyAction> {
	/** any default actions in this list will be not execute their default handling, use when you want to override the behavior */
	//this does not actually do anything, override defaultActionsHandlers to override this. Leaving here for now...
	// protected defaultActionExclusionList:Array<DefaultActionTypes> = [];

	/** include addItem as an "addAction" */
	protected addActions = {
		[FancyActionTypes.childAdded]: FancyActionTypes.childAdded,
		[DefaultActionTypes.addItem]: DefaultActionTypes.addItem,
	};
	/** ...also include removeItem as an "removeAction" */
	protected removeActions = {
		[FancyActionTypes.childRemoved]: FancyActionTypes.childRemoved,
		[DefaultActionTypes.removeItem]: DefaultActionTypes.removeItem,
	};

	/** built in handling of the DefaultActionHandlers for a split list */
	protected defaultActionsHandlers: DefaultActionHandlers<TModel, FancyAction, this> =
		new DefaultActionHandlers(this);

	/** creates a sublist that can be monitored, think selected tickets or edited ticket. This function cleans up some of the boilerplate */
	protected makeSimpleSublist(targetRenderType: TModelRenderTypes) {
		return monitorSubList<TModel>(
			this.rawEvent$,
			(action) => action.payload.currentRenderType === targetRenderType,
			(type) => this.isRemoveAction(type),
			(action) => action.payload
		);
	}
	constructor(protected angularLand, path, fancyJoins) {
		super(angularLand, path, FancyAction, fancyJoins);
	}

	/** more opinionated update, calls databaseUpdate on it's own */
	public $$updated(child: TModel, action: AngularFireAction<DatabaseSnapshot<any>>) {
		child.databaseUpdate(action.payload.val());
	}

	/** implements the defaults, can override and super this to add custom ones */
	public handleCustomEvents(baseObservable) {
		return baseObservable.pipe(
			mergeMap((action: FancyAction) => {
				const item = this.defaultActionsHandlers.handle(action);

				if (!item) {
					return EMPTY;
				} else if (item instanceof Observable) {
					return item;
				}
				return of(new FancyAction(item, action.type));
			})
		);
	}
}
